// Redeems a refresh token against Auth0 directly and hands the result to the Auth0 SPA SDK's local
// cache. This deliberately avoids the SDK's own refresh path: with useRefreshTokensFallback it falls
// back to silent iframe auth on invalid_grant, which could resume a *different* user's SSO session.
// The cache layout below mirrors @auth0/auth0-spa-js (see CacheManager / Auth0Client._saveEntryInCache).

const CACHE_PREFIX = '@@auth0spajs@@';
const ID_TOKEN_SUFFIX = '@@user@@';
const DEFAULT_SCOPE = 'openid profile email offline_access'; // keep in sync with Auth0Provider in index.tsx
const IS_AUTHENTICATED_COOKIE_MAX_AGE_S = 60 * 60 * 24;

// Auth0 refresh tokens expire 365 days after the original login.
export const REFRESH_TOKEN_LIFETIME_MS = 1000 * 60 * 60 * 24 * 365;

// Mirror of the Auth0 application's Refresh Token Rotation setting; enables the PIN re-wrap on rotation.
export const AUTH_REFRESH_TOKEN_ROTATE_ON = false;

const ID_TOKEN_NON_USER_CLAIMS = new Set([
  'iss', 'aud', 'exp', 'nbf', 'iat', 'jti', 'azp', 'nonce', 'auth_time', 'at_hash', 'c_hash', 'acr', 'amr',
  'sub_jwk', 'cnf', 'sip_from_tag', 'sip_date', 'sip_callid', 'sip_cseq_num', 'sip_via_branch', 'orig', 'dest',
  'mky', 'events', 'toe', 'txn', 'rph', 'sid', 'vot', 'vtm',
]); // prettier-ignore

export type Auth0TokenResponse = {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  scope?: string;
  expires_in: number;
  token_type?: string;
};

/** Auth0 rejected the token request; `error` is the OAuth error code, e.g. invalid_grant. */
export class Auth0TokenError extends Error {
  constructor(public error: string, description?: string) {
    super(description || error);
    this.name = 'Auth0TokenError';
  }
}

const clientId = () => process.env.REACT_APP_AUTH0_CLIENT_ID ?? '';
const audience = () => process.env.REACT_APP_AUTH0_AUDIENCE ?? 'default';

const sdkKeys = (): string[] =>
  Object.keys(localStorage).filter(key => key.startsWith(`${CACHE_PREFIX}::${clientId()}`));

/** The SDK's current refresh token, if a session is cached. Callers must not retain it. */
export const readSdkRefreshToken = (): string | undefined => {
  for (const key of sdkKeys()) {
    if (key.endsWith(ID_TOKEN_SUFFIX)) continue;
    try {
      const refreshToken = JSON.parse(localStorage.getItem(key) ?? '{}')?.body?.refresh_token;
      if (refreshToken) return refreshToken;
    } catch {
      // not a token entry
    }
  }
};

/** Email and org of the SDK's cached ID token, if a session is cached. */
export const readSdkUserIdentity = (): { email?: string; orgId?: string } | undefined => {
  try {
    const user = JSON.parse(localStorage.getItem(`${CACHE_PREFIX}::${clientId()}::${ID_TOKEN_SUFFIX}`) ?? '{}')
      ?.decodedToken?.user;
    return user && { email: user.email, orgId: user.org_id };
  } catch {
    return undefined;
  }
};

export const redeemRefreshToken = async (refreshToken: string): Promise<Auth0TokenResponse> => {
  const response = await fetch(`${process.env.REACT_APP_SAVI_API_HOST}/api/v2/users/oauth-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ grant_type: 'refresh_token', client_id: clientId(), refresh_token: refreshToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Auth0TokenError(body.error ?? 'unknown_error', body.error_description);
  return body as Auth0TokenResponse;
};

const decodeBase64Url = (segment: string): string => {
  const padded = segment
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(segment.length / 4) * 4, '=');
  return new TextDecoder().decode(Uint8Array.from(atob(padded), c => c.charCodeAt(0)));
};

const decodeIdToken = (idToken: string) => {
  const [, payload] = idToken.split('.');
  const claims: Record<string, unknown> = { __raw: idToken, ...JSON.parse(decodeBase64Url(payload)) };
  const user = Object.fromEntries(
    Object.entries(claims).filter(([claim]) => claim !== '__raw' && !ID_TOKEN_NON_USER_CLAIMS.has(claim)),
  );
  return { claims, user };
};

const setIsAuthenticatedCookie = () => {
  const attributes = ['path=/', `max-age=${IS_AUTHENTICATED_COOKIE_MAX_AGE_S}`];
  if (window.location.protocol === 'https:') attributes.push('secure', 'samesite=none');
  document.cookie = `auth0.${clientId()}.is.authenticated=true; ${attributes.join('; ')}`;
};

/**
 * Replaces any cached Auth0 session with one built from `tokens`, so that the next page load
 * (Auth0Provider -> checkSession) comes up authenticated as that user. Callers should reload.
 */
export const installAuth0Session = (tokens: Auth0TokenResponse): void => {
  sdkKeys().forEach(key => localStorage.removeItem(key));

  const decodedToken = decodeIdToken(tokens.id_token);
  const tokenKey = [CACHE_PREFIX, clientId(), audience(), DEFAULT_SCOPE].join('::');
  const idTokenKey = [CACHE_PREFIX, clientId(), ID_TOKEN_SUFFIX].join('::');

  const nowSeconds = Math.floor(Date.now() / 1000);
  const idTokenExp = typeof decodedToken.claims.exp === 'number' ? decodedToken.claims.exp : Infinity;

  localStorage.setItem(idTokenKey, JSON.stringify({ id_token: tokens.id_token, decodedToken }));
  localStorage.setItem(
    tokenKey,
    JSON.stringify({
      body: {
        access_token: tokens.access_token,
        token_type: tokens.token_type,
        expires_in: tokens.expires_in,
        refresh_token: tokens.refresh_token,
        scope: DEFAULT_SCOPE,
        audience: audience(),
        client_id: clientId(),
        ...(tokens.scope ? { oauthTokenScope: tokens.scope } : {}),
      },
      expiresAt: Math.min(nowSeconds + tokens.expires_in, idTokenExp),
    }),
  );
  localStorage.setItem(`${CACHE_PREFIX}::${clientId()}`, JSON.stringify({ keys: [tokenKey, idTokenKey] }));

  setIsAuthenticatedCookie();
};
